import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  toggleEditMode,
  updateWidgetProperty,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { EditorContext } from "../../../../index";

import { Property } from "../../../../components/properties";
import { Fonts, Widgets } from "@paperflite-marketing/cleverstory-commons";
import { Font } from "../../../../components/properties/fonts";
import { Frame } from "scenejs";
import { FORM } from "constants/widgetTypes";
import { getColorsConfig } from "services/cssHelper.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons";

import styles from "./otpInput.module.scss";

const FormOtpInputWidget = function OtpInputEditor({
  className,
  style,
  widgetProps,
  widgetRef,
}) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );
  const properties = _.get(widgetProps, "properties", {});

  Fonts.useFont(_.get(widgetProps, "fonts") || []);

  var widgetStyles = {
    ...style,
    ...getFieldStyles(properties, breakpoint, "regular"),
  };

  return (
    <div
      id={id}
      ref={ref}
      className={cx(className, styles.inputField)}
      style={widgetStyles}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      <Widgets.FormOtpInputWidget
        className={styles.textWidget}
        widgetProps={{
          ...widgetProps,
          editorView: true
        }}
        style={{
          ...style,
          transform: `translate(0px, 0px) rotate(0deg) scale(1, 1)`,
        }}
        breakpoint={breakpoint}
      />
    </div>
  );
};

const OtpInputWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "uid");
  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === id;
  });

  const handleEditMode = () => {
    dispatch(toggleEditMode(editorSession, props.uid, true));
    setView(null);
  };

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action onClick={handleEditMode.bind(this, "text")}>
        Edit Field
      </Property.Action>
      <Property.Divider />
      <Property.Action tooltip='Layout' onClick={() => changeView("layout")}>
        <i className={"cleverstory-layout"} />
      </Property.Action>
      <Property.Action tooltip='Style' onClick={() => changeView("style")}>
        <i className={"cleverstory-customize"} />
      </Property.Action>

      {editMode && (
        <OtpInputEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={id}
          editor={editorSession}
          breakpoint={"desktop"}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}

      {!editMode && view === "style" && (
        <FieldDesignEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

const OtpInputWidgetMobileAction = ({ editorSession, uid, ...rest }) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editMode = useSelector((state) => {
    return _.get(state, `${editorSession}.present.editMode`) === uid;
  });

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(view);
  };

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, uid, false));
    setView(null);
  };

  return (
    <>
      <Property.Action tooltip='Layout' onClick={() => changeView("layout")}>
        <FontAwesomeIcon icon={faSlidersH} />
      </Property.Action>

      {!editMode && view === "layout" && (
        <AlignmentEditor
          id={uid}
          editor={editorSession}
          breakpoint={"mobile"}
          widget={rest}
          onCancel={() => {
            closePopup();
          }}
        />
      )}
    </>
  );
};

FormOtpInputWidget.moveableConfig = (editorSession, widget) => {
  const widgetElement = document.querySelector(
    `#${editorSession} .editor_widget_renderer #${widget.uid}`
  );
  const formElement = widgetElement?.closest(`div[data-widget-type="${FORM}"]`);
  const pageElement = widgetElement?.closest(`.editor_widget_scroll_container`);

  const rect = formElement?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const bounds = {
    left: left,
    right: left + rect?.width,
    top: top,
    bottom: top + rect?.height,
  };

  return {
    draggable: true,
    resizable: false,
    rotatable: true,
    selectable: true,
    cloneable: false,
    deletable: false,
    renderDirections: ["e", "w"],
    bounds: bounds,
    onReparent: (widget, parent) => {
      return false;
    },
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set("width", "auto");
      frame.set("height", "auto");
      return frame.get();
    },
  };
};

FormOtpInputWidget.actions = (editor, breakpoint, props) => {
  return (
    <>
      {breakpoint === "desktop" && (
        <OtpInputWidgetDesktopAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}

      {breakpoint === "mobile" && (
        <OtpInputWidgetMobileAction
          editorSession={editor}
          breakpoint={breakpoint}
          {...props}
        />
      )}
    </>
  );
};

/**
 * Field input editor
 *
 */
const OtpInputEditor = (props) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const id = _.get(props, "widget.uid");
  const placeholder =
    _.get(props, "widget.properties.desktop.placeholder") || "";

  const handlePlaceholderChange = (e) => {
    let value = e.target.value;
  
    if (value.length > 1) {
      value = value.charAt(0);
    }
  
    dispatch(
      updateWidgetProperty(editorSession, id, "desktop", {
        placeholder: value,
      })
    );
  };

  return (
    <Property.Dialog
      width={222}
      target={`#${id}`}
      title="Field Settings"
      onCancel={props.onCancel}
    >
      <Property name="Placeholder OTP">
        <Property.Input
          value={placeholder}
          onChange={handlePlaceholderChange}
          placeholder="Enter Placeholder OTP"
        />
      </Property>
    </Property.Dialog>
  );
};

/**
 * Alignment Editor
 *
 */
const AlignmentEditor = ({ id, widget, breakpoint, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const inputWidth =
    _.get(widget, `properties.${breakpoint}.styles.layout.width`) || 50;
    const inputHeight =
    _.get(widget, `properties.${breakpoint}.styles.layout.height`) || 50;
  const fieldSpacing =
    _.get(widget, `properties.${breakpoint}.styles.layout.fieldSpacing`) || 0;

  const handlePaddingChange = (field, value) => {
    var request = _.get(widget, `properties.${breakpoint}.styles`) || {};

    request = _.cloneDeep(request);
    _.set(request, `layout.${field}`, value);
    handleChange(request);
  };

  const handleChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  return (
    <Property.Dialog
      className={styles.fieldLayoutDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Layout"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Width" className={styles.marginProperty}>
          <Property.Slider
            min={50}
            max={100}
            step={1}
            input={true}
            value={inputWidth}
            onChange={(value) => handlePaddingChange("width", value)}
          />
        </Property>
        <Property name="Height" className={styles.marginProperty}>
          <Property.Slider
            min={50}
            max={100}
            step={1}
            input={true}
            value={inputHeight}
            onChange={(value) => handlePaddingChange("height", value)}
          />
        </Property>
        <Property
          name="Space between inputs"
          className={styles.marginProperty}
        >
          <Property.Slider
            min={0}
            max={100}
            step={1}
            input={true}
            value={fieldSpacing}
            onChange={(value) => handlePaddingChange("fieldSpacing", value)}
          />
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
};

/**
 * Field design editor
 *
 */
const FieldDesignEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector((state) =>
    _.get(state, `${editorSession}.present.breakpoint`)
  );

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const fieldStyles = _.get(properties, `styles.regular`, {});

  const border = _.get(fieldStyles, "border", {});
  const borderColor = _.get(border, "color", null);
  const borderStyle = _.get(border, "style") || "bottom";
  const borderWidth = _.get(border, "width", 0);
  const borderRadius = _.get(border, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });

  const labelStyles = _.get(fieldStyles, "label");
  const inputStyles = _.get(fieldStyles, "input");

  const handlePropertyChange = (value) => {
    dispatch(
      updateWidgetProperty(editorSession, id, breakpoint, {
        styles: value,
      })
    );
  };

  const handleBorderColorChange = (color) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.border.color`, color);
    handlePropertyChange(request);
  };

  const handleBorderStyleChange = (style) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.border.style`, style);
    handlePropertyChange(request);
  };

  const handleBorderWidthChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.border.width`, value);
    handlePropertyChange(request);
  };

  const handleBorderRadiusChange = (radius) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.border.radius`, radius);
    handlePropertyChange(request);
  };

  const handleInputStyleChange = (value) => {
    var request = _.get(properties, `styles`);
    _.set(request, `regular.input`, value);
    handlePropertyChange(request);
  };

  return (
    <Property.Dialog
      className={styles.fieldDesignDialog}
      width={246}
      target={`#${id}`}
      title={"Customize Field Design"}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <div className={styles.fieldDesignProperty}>
          <Property.Accordion
            title={"Border"}
            className={styles.borderProperty}
          >
            <Property
              name="Border Style"
              className={styles.inputTextStyleProperty}
            >
              <ul className={styles.borderStyles}>
                <li
                  className={cx({ [styles.active]: borderStyle === "none" })}
                  onClick={() => handleBorderStyleChange("none")}
                >
                  None
                </li>
                <li
                  className={cx({ [styles.active]: borderStyle === "all" })}
                  onClick={() => handleBorderStyleChange("all")}
                >
                  All Side
                </li>
                <li
                  className={cx({ [styles.active]: borderStyle === "bottom" })}
                  onClick={() => handleBorderStyleChange("bottom")}
                >
                  Bottom
                </li>
              </ul>
            </Property>

            {borderStyle !== "none" && (
              <>
                <Property.Color
                  title={"Color"}
                  clearable={false}
                  value={borderColor}
                  onChange={handleBorderColorChange}
                />
                <Property
                  name="Stroke Width"
                  className={styles.inputTextStyleProperty}
                >
                  <Property.Slider
                    min={0}
                    max={10}
                    step={1}
                    input
                    value={borderWidth}
                    onChange={handleBorderWidthChange}
                  />
                </Property>
              </>
            )}

            <Property
              name="Corner Radius"
              className={styles.inputTextStyleProperty}
            >
              <Property.BorderRadius
                value={borderRadius}
                onChange={handleBorderRadiusChange}
              />
            </Property>
          </Property.Accordion>
          <FontEditor
            editorSession={editorSession}
            id={id}
            title={"Input Text"}
            type={"input"}
            value={inputStyles}
            onChange={handleInputStyleChange}
          />
        </div>
      </Property.Body>
    </Property.Dialog>
  );
};

const FontEditor = ({ editorSession, id, title, type, value, onChange }) => {
  const dispatch = useDispatch();

  const background = _.get(value, "background");
  const color = _.get(value, "color");
  const placeholderColor = _.get(value, "placeholderColor");
  const fontSize = _.get(value, "font.fontSize") || 14;

  const handleBackgroundChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "background", color);
    onChange(request);
  };

  const handleColorChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "color", color);
    onChange(request);
  };

  const handlePlaceholderColorChange = (color) => {
    var request = _.cloneDeep(value);
    _.set(request, "placeholderColor", color);
    onChange(request);
  };

  const handleFontSizeChange = (size) => {
    var request = _.cloneDeep(value);
    _.set(request, "font.fontSize", size);
    onChange(request);
  };

  return (
    <>
      <Property.Accordion title={title} className={styles.textProperty}>
        {type === "input" && (
          <Property.Color
            title={"Background"}
            gradient={true}
            clearable={true}
            value={background}
            onChange={handleBackgroundChange}
          />
        )}

        <Property.Color
          title={type === "input" ? "Input Color" : "Label Color"}
          value={color}
          onChange={handleColorChange}
        />

        {type === "input" && (
          <Property.Color
            title={"Placeholder Color"}
            value={placeholderColor}
            onChange={handlePlaceholderColorChange}
          />
        )}
        <Font.Size
          value={fontSize}
          onChange={handleFontSizeChange}
          className={styles.inputTextStyleProperty}
        />
      </Property.Accordion>
    </>
  );
};

/**
 * --------------------------------------------------
 * Field Styles
 * --------------------------------------------------
 */
function getFieldStyles(properties, breakpoint) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const deviceProperties = _.get(properties, `${breakpoint}`);

  //alignment
  const alignment =
    _.get(deviceProperties, "styles.layout.alignment") || "left";
  styles["alignItems"] = alignment;

  if (alignment === "left") {
    styles["alignItems"] = "flex-start";
  } else if (alignment === "right") {
    styles["alignItems"] = "flex-end";
  }

  return styles;
}

/**
 * --------------------------------------------------
 * Placeholder Styles
 * --------------------------------------------------
 */
export function getPlaceholderColor(properties) {
  var styles = {};

  if (!properties) {
    return styles;
  }

  const defaultProperties = _.get(properties, `desktop`);
  const inputStyles = _.get(defaultProperties, "styles.regular.input");

  const colorRgb = _.get(inputStyles, "placeholderColor", null);
  if (!colorRgb) {
    return null;
  }

  return `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
}

export function getColorStyles(value) {
  if (!value) {
    return null;
  }

  const colorRgb = value;

  var color = "inherit";
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  var styles = {
    color: color,
  };

  return styles;
}

export function getBorderStyles(border) {
  var styles = {};
  if (!border) {
    return styles;
  }

  const topLeft = _.get(border, "radius.topLeft", null);
  const topRight = _.get(border, "radius.topRight", null);
  const bottomLeft = _.get(border, "radius.bottomLeft", null);
  const bottomRight = _.get(border, "radius.bottomRight", null);

  if (topLeft) {
    styles["borderTopLeftRadius"] = parseInt(topLeft) + "px";
  }

  if (topRight) {
    styles["borderTopRightRadius"] = parseInt(topRight) + "px";
  }

  if (bottomLeft) {
    styles["borderBottomLeftRadius"] = parseInt(bottomLeft) + "px";
  }

  if (bottomRight) {
    styles["borderBottomRightRadius"] = parseInt(bottomRight) + "px";
  }

  const borderStyle = _.get(border, "style") || "bottom";
  if (borderStyle !== "none") {
    const borderWidthString = _.get(border, "width") || 0;
    var borderWidget = parseInt(borderWidthString) + "px";

    const fieldName =
      borderStyle == "all" ? "borderWidth" : "borderBottomWidth";
    styles[fieldName] = borderWidget;

    const colorRgb = _.get(border, "color");
    var color = "transparent";
    if (colorRgb) {
      color = getColorsConfig(colorRgb);
    }

    styles["borderColor"] = color;
  }

  return styles;
}

export function getFontStyle(fontStyles) {
  var styles = {};

  if (!fontStyles) {
    return styles;
  }

  //background-color
  const backgroundColorRgb = _.get(fontStyles, "background", null);
  if (backgroundColorRgb) {
    styles = {
      ...styles,
      background: getColorsConfig(backgroundColorRgb),
    };
  }

  //font-color
  const colorRgb = _.get(fontStyles, "color", null);
  const color = getColorStyles(colorRgb);
  styles = {
    ...styles,
    ...color,
  };

  //font-family
  const fontFamily = _.get(fontStyles, "font.fontFamily", "inherit");
  styles = {
    ...styles,
    fontFamily: fontFamily,
  };

  //font-size
  const fontSizeString = _.get(fontStyles, `font.fontSize`) || 14;
  if (fontSizeString) {
    var fontSize = parseInt(fontSizeString) + "px";
    styles["fontSize"] = fontSize;
  }

  return styles;
}

FormOtpInputWidget.processRequest = (widget) => {
  if(_.get(widget, 'content.id')){
    widget = {
      ...widget,
      content: _.get(widget, 'content.id')
    }
  }
  return widget;
};

export { FormOtpInputWidget };
